import "permissions/__extensions/users/user-extensions";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import type { User } from "users/user";

/**
 * The signed-in user, carrying the grants the session has loaded for them.
 *
 * Views ask the user what they may do — `user?.canAccessWork()` — so nothing is threaded through a
 * call and no answer is parked in a variable. This hook is what lets that work. It does two things:
 * it subscribes the caller to the permissions store, so the nav or the button repaints when the
 * grants arrive, and it hands back the user seen as the holder of those grants.
 *
 * The result is derived rather than the session's own instance mutated. The session's User is
 * shared by everything that reads the session, and writing to it during a render is the impurity
 * the React Compiler refuses; `withSessionPermissions` puts the grants on a fresh object whose
 * prototype is that user, so nothing is copied and nothing is disturbed.
 *
 * Use this anywhere a capability decides what to draw. Reading `session.user` directly is still
 * right for identity — a name, an id — but its capabilities answer false, because that instance
 * deliberately never carries grants.
 */
export default function useSessionUser(): User | null {
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const user = session.user;

  return useMemo(() => user?.withSessionPermissions(sessionPermissions) ?? null, [user, sessionPermissions]);
}
